import React, { FormEvent, KeyboardEvent, useMemo } from 'react';

import styled from 'styled-components';

import { FieldNamesMarkedBoolean, UseFormRegister, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import UIButton, { EUIButtonVariant } from '@aquariuxfintech/aqx-trader-react-ui/button';
import UIFormItem, { EUIFormItemVariant } from '@aquariuxfintech/aqx-trader-react-ui/form';
import { ESpacing, ETypography } from '@aquariuxfintech/aqx-trader-react-ui/foundation';
import UIInput, { EUIInputVariant } from '@aquariuxfintech/aqx-trader-react-ui/input';
import UISelectPicker, { EUISelectPickerVariant } from '@aquariuxfintech/aqx-trader-react-ui/select-picker';
import UITab, { EUITabVariant } from '@aquariuxfintech/aqx-trader-react-ui/tab';
import UIText, { EUITextVariant } from '@aquariuxfintech/aqx-trader-react-ui/text';

import { ELoginAs } from '@aqx-core/common/api/auth';
import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { EInputType } from '@aqx-core/common/hooks/form/use-otp-login-state';
import { IOtpLoginFormParams } from '@aqx-core/common/services/auth';
import { useSignUpDemo } from '@aqx-core/common/services/sign-up-demo';
import { IAppTheme } from '@aqx-core/common/theme';

import LoginSignUpAction from '../sign-up';

interface IOtpLoginStep1Props {
  tabs: { label: string; value: ELoginAs }[];
  selectedTab: ELoginAs | undefined;
  onTabChange: (nextValue: ELoginAs) => void;
  theme: IAppTheme;
  authLoginLoading: boolean | undefined;
  register: UseFormRegister<IOtpLoginFormParams>;
  setValue: UseFormSetValue<IOtpLoginFormParams>;
  trigger: UseFormTrigger<IOtpLoginFormParams>;
  userId: string;
  dialCode?: string;
  phoneNumber?: string;
  touchedFields: Partial<FieldNamesMarkedBoolean<IOtpLoginFormParams>>;
  handleKeyDown: (event: KeyboardEvent) => void;
  inputType: EInputType;
  isStep1BtnDisabled: boolean;
  onOtpLogin: () => void;
  onPasswordLogin: () => void;
  onFormSubmit: (e: FormEvent) => void;
}

const OtpLoginStep1: FCC<IOtpLoginStep1Props> = ({
  tabs,
  selectedTab,
  onTabChange,
  theme,
  authLoginLoading,
  register,
  setValue,
  trigger,
  userId,
  dialCode,
  phoneNumber,
  touchedFields,
  handleKeyDown,
  inputType,
  isStep1BtnDisabled,
  onOtpLogin,
  onPasswordLogin,
  onFormSubmit
}) => {
  const { t } = useTranslation();
  const { countries } = useSignUpDemo();

  const countryOptions = useMemo(() => {
    return countries.map(({ dialCode, country }) => ({
      label: `${country} (${dialCode})`,
      value: dialCode
    }));
  }, [countries]);

  const userIdInputLabel = t('Email/Phone Number');
  const userIdInputPlaceholder = t('Enter your email or phone number (without country code)');

  // Determine if we should show validation error for email/unknown mode
  const showUserIdValidationError = inputType === EInputType.UNKNOWN && userId && touchedFields.userId;

  // Determine if we should show validation error for phone mode
  const showPhoneValidationError =
    inputType === EInputType.PHONE && phoneNumber && touchedFields.phoneNumber && !/^\d+$/.test(phoneNumber);

  // Single unified error message
  const validationErrorMessage = t('Enter a valid email or phone number.');

  return (
    <>
      <StyledFormContainer onSubmit={onFormSubmit}>
        <UIText typography={ETypography.DISPLAY_M_BOLD}>{t('Log in')}</UIText>
        <UITab
          variant={EUITabVariant.UNDERLINED}
          tabList={tabs}
          selectedValue={selectedTab}
          onClick={onTabChange}
          backgroundColor={theme.themeColors.background.default}
          stretch
          dataTestId={EDataTestId.LOGIN_ACCOUNT_TYPE}
          disabled={authLoginLoading}
        />

        <StyledFormInputsContainer>
          {inputType === EInputType.PHONE ? (
            <StyledPhoneFieldContainer>
              <UIText typography={ETypography.TEXT_L_REGULAR}>{userIdInputLabel}</UIText>
              <StyledInputSelectContainer>
                <StyledSelectorContainer>
                  <UIFormItem variant={EUIFormItemVariant.ITEM_WITH_FIELD_LABEL} size="large">
                    <UISelectPicker<string>
                      variant={EUISelectPickerVariant.SEARCHABLE_SELECTOR}
                      onSelect={(val) => {
                        setValue('dialCode', val);
                        trigger('dialCode');
                      }}
                      placeholder="+XX"
                      options={countryOptions}
                      selectedValue={dialCode}
                      error={undefined}
                      dataTestId={EDataTestId.SIGN_UP_COUNTRY_SELECTOR}
                      size="large"
                      dropdownWidth="240px"
                      dropDownMaxHeight={200}
                      valueLabel={dialCode}
                      useLabelToSearch
                      searchPlaceholder={t('Search')}
                    />
                  </UIFormItem>
                </StyledSelectorContainer>
                <UIFormItem variant={EUIFormItemVariant.ITEM_WITH_FIELD_LABEL} size="large">
                  <UIInput
                    {...register('phoneNumber')}
                    variant={EUIInputVariant.BORDERED_TEXT}
                    placeholder={t('Enter your phone number')}
                    value={phoneNumber}
                    error={showPhoneValidationError ? ' ' : undefined}
                    onKeyDown={handleKeyDown}
                    dataTestId={EDataTestId.LOGIN_USER_ID}
                    size="large"
                  />
                </UIFormItem>
              </StyledInputSelectContainer>
              {showPhoneValidationError && <UIText variant={EUITextVariant.ERROR} error={validationErrorMessage} />}
            </StyledPhoneFieldContainer>
          ) : (
            <UIFormItem variant={EUIFormItemVariant.ITEM_WITH_FIELD_LABEL} label={userIdInputLabel} size="large">
              <UIInput
                {...register('userId')}
                variant={EUIInputVariant.BORDERED_TEXT}
                placeholder={userIdInputPlaceholder}
                value={userId}
                error={showUserIdValidationError ? ' ' : undefined}
                onKeyDown={handleKeyDown}
                dataTestId={EDataTestId.LOGIN_USER_ID}
                size="large"
              />
              {showUserIdValidationError && <UIText variant={EUITextVariant.ERROR} error={validationErrorMessage} />}
            </UIFormItem>
          )}
        </StyledFormInputsContainer>

        <StyledButtonContainer>
          <UIButton
            variant={EUIButtonVariant.FILLED_THEMED}
            loading={authLoginLoading}
            dataTestId={EDataTestId.LOGIN_SUBMIT}
            size="large"
            disabled={isStep1BtnDisabled}
            onClick={onOtpLogin}
          >
            {inputType === EInputType.UNKNOWN ? t('Log in') : t('Log in with OTP')}
          </UIButton>

          {inputType === EInputType.EMAIL && (
            <UIButton
              variant={EUIButtonVariant.FILLED_THEMED}
              dataTestId={EDataTestId.LOGIN_SUBMIT}
              size="large"
              disabled={isStep1BtnDisabled}
              onClick={onPasswordLogin}
            >
              {t('Log in with Password')}
            </UIButton>
          )}
        </StyledButtonContainer>
      </StyledFormContainer>
      <LoginSignUpAction selectedTab={selectedTab} />
    </>
  );
};

export default OtpLoginStep1;

const StyledFormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${ESpacing.XXXL}px;
  width: 512px;
`;

const StyledButtonContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${ESpacing.M}px;
  margin-top: -${ESpacing.M}px;
`;

const StyledFormInputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${ESpacing.XL}px;
`;

const StyledPhoneFieldContainer = styled.div``;

const StyledInputSelectContainer = styled.div`
  display: flex;
  margin-top: ${ESpacing.XXS}px;
`;

const StyledSelectorContainer = styled.div`
  width: 92px;
`;
